import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, BriefcaseBusiness, ChevronDown, ChevronRight, FolderPlus, Plus } from "lucide-react"
import { toast } from "sonner"
import { OrgChartTree } from "@/components/organization/OrgChartTree"
import { OrgUnitFormDialog } from "@/components/organization/OrgUnitFormDialog"
import { OrgUnitMoveDialog } from "@/components/organization/OrgUnitMoveDialog"
import { PositionSheet } from "@/components/organization/PositionSheet"
import { WorkplaceSheet } from "@/components/organization/WorkplaceSheet"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { fmtEuro } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type { DecimalString, OrgChartNode, Position, Workplace } from "@/types"

/** Baum flach machen (id, name, level) — Grundlage fuer die Zielauswahl beim Verschieben. */
function flattenTree(roots: OrgChartNode[]): { id: string; name: string; level: number }[] {
  const out: { id: string; name: string; level: number }[] = []
  function walk(node: OrgChartNode) {
    out.push({ id: node.id, name: node.name, level: node.level })
    node.children.forEach(walk)
  }
  roots.forEach(walk)
  return out
}

/** IDs einer Einheit + all ihrer Nachfahren (fuer die Verschieben-Zielsperre, Zyklus-Schutz clientseitig gespiegelt). */
function collectSelfAndDescendants(roots: OrgChartNode[], unitId: string): Set<string> {
  const ids = new Set<string>()
  function findAndCollect(node: OrgChartNode): boolean {
    if (node.id === unitId) {
      collectIds(node)
      return true
    }
    return node.children.some(findAndCollect)
  }
  function collectIds(node: OrgChartNode) {
    ids.add(node.id)
    node.children.forEach(collectIds)
  }
  roots.forEach(findAndCollect)
  return ids
}

function fmtRate(value: DecimalString | null): string {
  return value != null && value !== "" ? fmtEuro(Number(value)) : "—"
}

function fmtPct(value: DecimalString | null): string {
  return value != null && value !== ""
    ? `${Number(value).toLocaleString("de-DE", { minimumFractionDigits: 2 })} %`
    : "—"
}

/** Sortier-/filterbarer Zahlwert fuer Dezimal-Strings (leer → null). */
function numOrNull(value: DecimalString | null): number | null {
  return value != null && value !== "" ? Number(value) : null
}

export function OrganizationPage() {
  const canWrite = useCan("personal_schreiben")
  const canDelete = useCan("loeschen")
  const queryClient = useQueryClient()
  const [tab, setTab] = useState("organigramm")
  const [unassignedOpen, setUnassignedOpen] = useState(false)

  // Gemeinsame Stammdaten-Queries (Lookups fuer alle drei Tabs)
  const orgChartQuery = useQuery({
    queryKey: ["org-chart"],
    queryFn: () => api.organization.orgChart(),
  })
  const positionsQuery = useQuery({
    queryKey: ["positions"],
    queryFn: () => api.organization.positions.list({ size: 100 }),
  })
  const workplacesQuery = useQuery({
    queryKey: ["workplaces"],
    queryFn: () => api.organization.workplaces.list({ size: 100 }),
  })
  const orgUnitsQuery = useQuery({
    queryKey: ["org-units"],
    queryFn: () => api.organization.orgUnits.list({ size: 100 }),
  })
  const jobDescriptionsQuery = useQuery({
    queryKey: ["job-descriptions"],
    queryFn: () => api.organization.jobDescriptions.list({ size: 100 }),
  })

  const positions = positionsQuery.data?.data ?? []
  const workplaces = useMemo(() => workplacesQuery.data?.data ?? [], [workplacesQuery.data])
  const workplaceNameById = useMemo(
    () => new Map(workplaces.map((w) => [w.id, w.name])),
    [workplaces]
  )

  // Sheet-Zustaende
  const [selectedNode, setSelectedNode] = useState<OrgChartNode | null>(null)
  // Zuletzt im Organigramm selektierter Knoten — bleibt (anders als selectedNode,
  // das mit dem modalen Knoten-Sheet wieder auf null geht) ueber das Schliessen
  // des Sheets hinaus erhalten und steuert Baum-Highlight + Vorbelegung von
  // "+ Neue Stelle" im Stellen-Tab.
  const [lastSelectedUnit, setLastSelectedUnit] = useState<{ id: string; name: string } | null>(
    null
  )
  function selectNode(node: OrgChartNode) {
    setSelectedNode(node)
    setLastSelectedUnit({ id: node.id, name: node.name })
  }
  // defaultOrgUnitId: Vorbelegung der Organisationseinheit bei Neuanlage
  // ("Stelle hinzufügen" am Knoten bzw. "+ Neue Stelle" bei selektiertem Knoten).
  // Fachliche Regel: "Geschäftsführung" ist eine gewöhnliche Stelle an der
  // obersten Einheit — kein Sonder-Flag, keine Sonderbehandlung.
  const [positionSheet, setPositionSheet] = useState<{
    open: boolean
    position: Position | null
    defaultOrgUnitId: string | null
  }>({ open: false, position: null, defaultOrgUnitId: null })
  const [workplaceSheet, setWorkplaceSheet] = useState<{
    open: boolean
    workplace: Workplace | null
  }>({ open: false, workplace: null })

  // Baum-Pflege-Dialoge (nur canWrite)
  const [formDialog, setFormDialog] = useState<{
    open: boolean
    mode: "create" | "rename"
    target: { id: string | null; name: string } | null
  }>({ open: false, mode: "create", target: null })
  const [moveDialog, setMoveDialog] = useState<{
    open: boolean
    unit: { id: string; name: string } | null
  }>({ open: false, unit: null })

  const roots = orgChartQuery.data?.roots ?? []
  const flatUnits = useMemo(() => flattenTree(roots), [roots])

  // ANNAHME: Root-Vorauswahl beim ersten Laden — erster Wurzelknoten, damit rechts sofort
  // Inhalt steht (Punkt 2 des Auftrags). Nur einmal beim initialen Laden, nicht bei jedem
  // Refetch (sonst wuerde ein manuell abgewaehlter Knoten nach Invalidierung zurückspringen).
  const [rootPreselected, setRootPreselected] = useState(false)
  useEffect(() => {
    if (rootPreselected || selectedNode !== null) return
    if (roots.length > 0) {
      selectNode(roots[0])
      setRootPreselected(true)
    }
  }, [roots, rootPreselected, selectedNode])

  const nodePositions = selectedNode
    ? positions.filter((p) => p.orgUnitId === selectedNode.id)
    : []

  const unassignedPositions = useMemo(
    () => positions.filter((p) => p.orgUnitId === null),
    [positions]
  )

  // Grid-Spalten Stellen: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const positionColumns = useMemo<ColumnDef<Position>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: (p) => p.name,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "workplace",
        header: "Arbeitsplatz",
        accessorFn: (p) => (p.workplaceId ? (workplaceNameById.get(p.workplaceId) ?? "—") : "—"),
      },
      {
        id: "surchargeRate",
        header: "Zulagensatz",
        accessorFn: (p) => numOrNull(p.surchargeRate),
        cell: ({ row }) => fmtRate(row.original.surchargeRate),
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "visibleRoles",
        header: "Sichtbare Rollen",
        accessorFn: (p) => (p.visibleRoles ?? []).join(", "),
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {(row.original.visibleRoles ?? []).map((r) => (
              <Badge key={r} variant="secondary">
                {r}
              </Badge>
            ))}
          </div>
        ),
      },
    ],
    [workplaceNameById]
  )

  // Grid-Spalten Arbeitsplaetze (Zulagen je Erschwernis als €/h- und %-Paar)
  const workplaceColumns = useMemo<ColumnDef<Workplace>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: (w) => w.name,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "noiseRate",
        header: "Lärm €/h",
        accessorFn: (w) => numOrNull(w.noiseSurchargeRate),
        cell: ({ row }) => fmtRate(row.original.noiseSurchargeRate),
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "noisePct",
        header: "Lärm %",
        accessorFn: (w) => numOrNull(w.noiseSurchargePct),
        cell: ({ row }) => fmtPct(row.original.noiseSurchargePct),
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "acidDustRate",
        header: "Säure/Staub €/h",
        accessorFn: (w) => numOrNull(w.acidDustSurchargeRate),
        cell: ({ row }) => fmtRate(row.original.acidDustSurchargeRate),
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "acidDustPct",
        header: "Säure/Staub %",
        accessorFn: (w) => numOrNull(w.acidDustSurchargePct),
        cell: ({ row }) => fmtPct(row.original.acidDustSurchargePct),
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "displacementRate",
        header: "Verdrängung €/h",
        accessorFn: (w) => numOrNull(w.displacementSurchargeRate),
        cell: ({ row }) => fmtRate(row.original.displacementSurchargeRate),
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "displacementPct",
        header: "Verdrängung %",
        accessorFn: (w) => numOrNull(w.displacementSurchargePct),
        cell: ({ row }) => fmtPct(row.original.displacementSurchargePct),
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
    ],
    []
  )

  const deleteUnitMutation = useMutation({
    mutationFn: (id: string) => api.organization.orgUnits.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-chart"] })
      queryClient.invalidateQueries({ queryKey: ["org-units"] })
      toast.success("Organisationseinheit gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function handleDeleteUnit(unit: {
    id: string
    name: string
    hasChildren: boolean
    positionCount: number
  }) {
    if (unit.hasChildren || unit.positionCount > 0) return // UI-Guard, Backend prueft ohnehin
    if (window.confirm(`„${unit.name}“ wirklich löschen?`)) {
      deleteUnitMutation.mutate(unit.id)
      if (selectedNode?.id === unit.id) setSelectedNode(null)
      if (lastSelectedUnit?.id === unit.id) setLastSelectedUnit(null)
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Organisation</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="organigramm">Organigramm</TabsTrigger>
          <TabsTrigger value="stellen">Stellen</TabsTrigger>
          <TabsTrigger value="arbeitsplaetze">Arbeitsplätze</TabsTrigger>
        </TabsList>

        {/* --- Organigramm ------------------------------------------------- */}
        <TabsContent value="organigramm">
          <div className="space-y-3">
            {canWrite && (
              <div className="flex justify-end">
                <Button
                  onClick={() => setFormDialog({ open: true, mode: "create", target: null })}
                >
                  <Plus /> Neue Wurzeleinheit
                </Button>
              </div>
            )}

            {unassignedPositions.length > 0 && (
              <Card>
                <CardContent className="py-3">
                  <button
                    type="button"
                    onClick={() => setUnassignedOpen((o) => !o)}
                    className="flex w-full items-center gap-2 text-left text-sm"
                  >
                    {unassignedOpen ? (
                      <ChevronDown className="size-4 shrink-0" />
                    ) : (
                      <ChevronRight className="size-4 shrink-0" />
                    )}
                    <AlertTriangle className="size-4 shrink-0 text-amber-500" />
                    <span>
                      <Badge variant="secondary">{unassignedPositions.length}</Badge>{" "}
                      {unassignedPositions.length === 1 ? "Stelle" : "Stellen"} ohne
                      Organigramm-Zuordnung
                    </span>
                  </button>
                  {unassignedOpen && (
                    <ul className="mt-2 space-y-1 border-t pt-2">
                      {unassignedPositions.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => setPositionSheet({ open: true, position: p, defaultOrgUnitId: null })}
                            className="w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          >
                            {p.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="py-4">
                {orgChartQuery.isLoading ? (
                  <LoadingSpinner fullPage />
                ) : orgChartQuery.isError ? (
                  <p className="text-sm text-destructive">{apiError(orgChartQuery.error)}</p>
                ) : roots.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Noch keine Organisationseinheiten angelegt.
                  </p>
                ) : (
                  <OrgChartTree
                    roots={roots}
                    selectedId={lastSelectedUnit?.id ?? null}
                    onSelect={selectNode}
                    positions={positions}
                    onSelectPosition={(p) => setPositionSheet({ open: true, position: p, defaultOrgUnitId: null })}
                    canWrite={canWrite}
                    canDelete={canDelete}
                    onCreateChild={(parent) =>
                      setFormDialog({ open: true, mode: "create", target: parent })
                    }
                    onAddPosition={(unit) =>
                      setPositionSheet({ open: true, position: null, defaultOrgUnitId: unit.id })
                    }
                    onRename={(unit) => setFormDialog({ open: true, mode: "rename", target: unit })}
                    onMove={(unit) => setMoveDialog({ open: true, unit })}
                    onDelete={handleDeleteUnit}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* --- Stellen ------------------------------------------------------ */}
        <TabsContent value="stellen">
          <div className="space-y-3">
            {canWrite && (
              <div className="flex justify-end">
                <Button
                  onClick={() =>
                    setPositionSheet({
                      open: true,
                      position: null,
                      // Ist im Organigramm ein Knoten selektiert, wird dessen
                      // Einheit vorbelegt (bleibt im Sheet änderbar).
                      defaultOrgUnitId: lastSelectedUnit?.id ?? null,
                    })
                  }
                >
                  <Plus /> Neue Stelle
                </Button>
              </div>
            )}
            <Card>
              <CardContent className="py-2">
                {positionsQuery.isLoading ? (
                  <LoadingSpinner fullPage />
                ) : (
                  <DataGrid
                    columns={positionColumns}
                    data={positions}
                    storageKey="organization-positions"
                    emptyMessage="Keine Stellen vorhanden."
                    onRowClick={(p) =>
                      setPositionSheet({ open: true, position: p, defaultOrgUnitId: null })
                    }
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* --- Arbeitsplaetze ---------------------------------------------- */}
        <TabsContent value="arbeitsplaetze">
          <div className="space-y-3">
            {canWrite && (
              <div className="flex justify-end">
                <Button onClick={() => setWorkplaceSheet({ open: true, workplace: null })}>
                  <Plus /> Neuer Arbeitsplatz
                </Button>
              </div>
            )}
            <Card>
              <CardContent className="py-2">
                {workplacesQuery.isLoading ? (
                  <LoadingSpinner fullPage />
                ) : (
                  <DataGrid
                    columns={workplaceColumns}
                    data={workplaces}
                    storageKey="organization-workplaces"
                    emptyMessage="Keine Arbeitsplätze vorhanden."
                    onRowClick={(w) => setWorkplaceSheet({ open: true, workplace: w })}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Knoten-Detail-Sheet (Organigramm) */}
      <Sheet
        open={selectedNode !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedNode(null)
        }}
      >
        <SheetContent sheetKey="org-unit-detail" className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedNode?.name}</SheetTitle>
            <SheetDescription>
              Ebene {selectedNode?.level}
              {selectedNode &&
                selectedNode.children.length > 0 &&
                ` · ${selectedNode.children.length} untergeordnete Einheit(en)`}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-3 py-4">
            {canWrite && selectedNode && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFormDialog({
                      open: true,
                      mode: "create",
                      target: { id: selectedNode.id, name: selectedNode.name },
                    })
                  }
                >
                  <FolderPlus /> Untereinheit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPositionSheet({
                      open: true,
                      position: null,
                      defaultOrgUnitId: selectedNode.id,
                    })
                  }
                >
                  <BriefcaseBusiness /> Stelle
                </Button>
              </div>
            )}
            <h3 className="text-sm font-semibold">Stellen in dieser Einheit</h3>
            {nodePositions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Keine Stellen dieser Einheit zugeordnet.
              </p>
            ) : (
              <ul className="space-y-2">
                {nodePositions.map((p) => (
                  <li key={p.id} className="rounded-md border p-2 text-sm">
                    <p className="font-medium">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.workplaceId
                        ? (workplaceNameById.get(p.workplaceId) ?? "—")
                        : "Kein Arbeitsplatz"}
                      {" · Zulagensatz "}
                      {fmtRate(p.surchargeRate)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <PositionSheet
        open={positionSheet.open}
        position={positionSheet.position}
        defaultOrgUnitId={positionSheet.defaultOrgUnitId}
        workplaces={workplaces}
        orgUnits={orgUnitsQuery.data?.data ?? []}
        jobDescriptions={jobDescriptionsQuery.data?.data ?? []}
        canWrite={canWrite}
        onOpenChange={(open) => setPositionSheet((s) => ({ ...s, open }))}
      />
      <WorkplaceSheet
        open={workplaceSheet.open}
        workplace={workplaceSheet.workplace}
        canWrite={canWrite}
        onOpenChange={(open) => setWorkplaceSheet((s) => ({ ...s, open }))}
      />

      <OrgUnitFormDialog
        open={formDialog.open}
        mode={formDialog.mode}
        targetUnit={formDialog.target}
        onOpenChange={(open) => setFormDialog((s) => ({ ...s, open }))}
        onDone={(unit) => {
          // Neu angelegte/umbenannte Einheit direkt selektieren, damit rechts sofort sichtbar
          selectNode({ id: unit.id, name: unit.name, level: 0, children: [] })
        }}
      />
      <OrgUnitMoveDialog
        open={moveDialog.open}
        unit={moveDialog.unit}
        flatUnits={flatUnits}
        disabledIds={
          moveDialog.unit ? collectSelfAndDescendants(roots, moveDialog.unit.id) : new Set()
        }
        onOpenChange={(open) => setMoveDialog((s) => ({ ...s, open }))}
      />
    </div>
  )
}
